import { useState } from "react";
import type { ReactNode } from "react";
import { ExternalLink, Eye } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader } from "@/components/shared/loader";
import { ContentAuditTimeline } from "@/features/content/components/ContentAuditTimeline";
import { ContentPreviewPanel } from "@/features/content/components/ContentPreviewPanel";
import { ContentVersionHistoryPanel } from "@/features/content/components/ContentVersionHistoryPanel";
import { contentService } from "@/features/content/services/contentService";
import { formatOptionalArabicDateTime } from "@/lib/formatters";
import { queryKeys } from "@/lib/query-keys";
import type { ContentEntry } from "@/types/content";

import { buildStrapiPageEditUrl } from "@/features/content/lib/strapi-page-url";

const REVIEW_STATUS_LABEL: Record<string, string> = {
  DRAFT: "مسودة",
  SUBMITTED: "قيد المراجعة",
  APPROVED: "موافَق عليه",
  REJECTED: "مرفوض",
};

function reviewVariant(status: string) {
  if (status === "APPROVED") return "default" as const;
  if (status === "REJECTED") return "destructive" as const;
  return "secondary" as const;
}

interface ContentDetailDialogProps {
  entry: ContentEntry;
  departmentName?: string;
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-medium">{value}</span>
    </div>
  );
}

export function ContentDetailDialog({
  entry,
  departmentName,
}: ContentDetailDialogProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "preview" | "history" | "audit">("details");
  const id = String(entry.id);

  const { data, isLoading, error } = useQuery({
    queryKey: [...queryKeys.content.detail(entry.contentType, id), "preview"],
    queryFn: () =>
      contentService.get(entry.contentType, id, { includePreview: true }),
    enabled: open,
  });

  const detail = data ?? entry;
  const reviewStatus =
    typeof detail.reviewStatus === "string" ? detail.reviewStatus : "DRAFT";
  const locale = typeof detail.locale === "string" ? detail.locale : undefined;
  const strapiHref = buildStrapiPageEditUrl(id, locale);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center text-muted-foreground transition-colors hover:text-primary"
          aria-label="عرض تفاصيل المحتوى"
          title="عرض"
        >
          <Eye className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-right">
            {detail.title ?? "تفاصيل المحتوى"}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : error ? (
          <p className="py-6 text-center text-sm text-destructive">
            تعذّر تحميل التفاصيل
          </p>
        ) : (
          <div>
            <TabsList className="mb-3 w-full">
              <TabsTrigger
                type="button"
                data-state={activeTab === "details" ? "active" : "inactive"}
                className="flex-1"
                onClick={() => setActiveTab("details")}
              >
                التفاصيل
              </TabsTrigger>
              <TabsTrigger
                type="button"
                data-state={activeTab === "preview" ? "active" : "inactive"}
                className="flex-1"
                onClick={() => setActiveTab("preview")}
              >
                المعاينة
              </TabsTrigger>
              <TabsTrigger
                type="button"
                data-state={activeTab === "history" ? "active" : "inactive"}
                className="flex-1"
                onClick={() => setActiveTab("history")}
              >
                النسخ
              </TabsTrigger>
              <TabsTrigger
                type="button"
                data-state={activeTab === "audit" ? "active" : "inactive"}
                className="flex-1"
                onClick={() => setActiveTab("audit")}
              >
                السجل
              </TabsTrigger>
            </TabsList>

            {activeTab === "details" && (
            <TabsContent>
              <div className="py-1 border-0 p-0">
                <Field label="العنوان" value={detail.title ?? "—"} />
                <Field label="الرابط (Slug)" value={detail.slug ?? "—"} />
                <Field
                  label="حالة المراجعة"
                  value={
                    <Badge variant={reviewVariant(reviewStatus)}>
                      {REVIEW_STATUS_LABEL[reviewStatus] ?? reviewStatus}
                    </Badge>
                  }
                />
                <Field
                  label="حالة النشر"
                  value={
                    detail.published ? (
                      <Badge variant="outline">منشور</Badge>
                    ) : (
                      <span className="text-muted-foreground">مسودة</span>
                    )
                  }
                />
                <Field label="الدائرة" value={departmentName ?? "—"} />
                <Field label="اللغة" value={locale ?? "—"} />
                <Field label="أُنشئ بواسطة" value={detail.authorName ?? "—"} />
                <Field
                  label="آخر تحديث"
                  value={formatOptionalArabicDateTime(detail.updatedAt)}
                />
                <Field
                  label="تاريخ النشر"
                  value={formatOptionalArabicDateTime(detail.publishedAt)}
                />
                {typeof detail.reviewNote === "string" && detail.reviewNote && (
                  <Field label="ملاحظة المراجعة" value={detail.reviewNote} />
                )}
              </div>
            </TabsContent>
            )}

            {activeTab === "preview" && (
            <TabsContent>
              <ContentPreviewPanel blocks={detail.blocks} layout={detail.layout} />
            </TabsContent>
            )}

            {activeTab === "history" && (
            <TabsContent>
              <ContentVersionHistoryPanel
                contentType={entry.contentType}
                pageId={id}
              />
            </TabsContent>
            )}

            {activeTab === "audit" && (
            <TabsContent>
              <ContentAuditTimeline pageId={id} />
            </TabsContent>
            )}
          </div>
        )}

        <DialogFooter>
          <a href={strapiHref} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm">
              <ExternalLink className="ml-1 size-3.5" />
              التعديل في نظام إدارة المحتوى (CMS)
            </Button>
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
